export const AUTH_IDLE_TIMEOUT_MS = 30 * 60 * 1000;

const AUTH_SESSION_KEY = 'auth_session_active';
const LAST_ACTIVITY_KEY = 'auth_last_activity';
const TOKEN_VERIFIED_KEY = 'auth_token_verified';
const AUTH_TOKEN_KEY = 'auth_session_token';
const AUTH_SUPERUSER_KEY = 'auth_is_superuser';
const AUTH_PERMISSIONS_KEY = 'auth_permissions';

export const AUTH_COOKIE_KEYS = [
  'myToken',
  'myName',
  'myUserId',
  'myCategory',
  'mySuperuser',
];

function isTruthyFlag(value) {
  return value === true || value === 1 || value === '1' || value === 'true' || value === 'True';
}

export function normalizePermissionList(value) {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
      return value.flatMap((entry) => (
        Object.entries(entry || {})
          .filter(([, flag]) => isTruthyFlag(flag))
          .map(([key]) => key)
      ));
    }
    return value.filter((entry) => typeof entry === 'string');
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return [];
    }
    try {
      const parsed = JSON.parse(trimmed);
      return normalizePermissionList(parsed);
    } catch {
      if (trimmed.includes(',')) {
        return trimmed.split(',').map((entry) => entry.trim()).filter(Boolean);
      }
      return [trimmed];
    }
  }
  return [];
}

function readStoredPermissions() {
  try {
    const raw = sessionStorage.getItem(AUTH_PERMISSIONS_KEY);
    if (!raw) {
      return [];
    }
    return normalizePermissionList(JSON.parse(raw));
  } catch {
    return [];
  }
}

function readStoredSuperuser() {
  return sessionStorage.getItem(AUTH_SUPERUSER_KEY) === '1';
}

export function establishAuthSession(token) {
  sessionStorage.setItem(AUTH_SESSION_KEY, '1');
  touchAuthActivity();
  if (token) {
    sessionStorage.setItem(AUTH_TOKEN_KEY, String(token));
    markAuthTokenVerified(token);
  }
}

export function persistAuthProfile(profile, setCookie, removeCookie) {
  const cookieOptions = { path: '/' };
  const isProfileSuperuser = isTruthyFlag(profile?.is_superuser);

  if (isProfileSuperuser) {
    sessionStorage.setItem(AUTH_SUPERUSER_KEY, '1');
    sessionStorage.removeItem(AUTH_PERMISSIONS_KEY);
    if (setCookie) {
      setCookie('mySuperuser', 'true', cookieOptions);
    }
    if (removeCookie) {
      removeCookie('myCategory', { path: '/' });
    }
    return;
  }

  sessionStorage.removeItem(AUTH_SUPERUSER_KEY);
  const permissions = normalizePermissionList(profile?.category);
  sessionStorage.setItem(AUTH_PERMISSIONS_KEY, JSON.stringify(permissions));
  if (setCookie) {
    setCookie('myCategory', permissions, cookieOptions);
  }
  if (removeCookie) {
    removeCookie('mySuperuser', { path: '/' });
  }
}

export function getResolvedAuthToken(cookies) {
  const sessionToken = sessionStorage.getItem(AUTH_TOKEN_KEY) || '';
  if (!sessionStorage.getItem(AUTH_SESSION_KEY) || !sessionToken) {
    return '';
  }
  if (typeof cookies === 'string') {
    return cookies === sessionToken ? cookies : '';
  }
  if (cookies?.myToken && cookies.myToken !== sessionToken) {
    return '';
  }
  return sessionToken;
}

export function touchAuthActivity() {
  if (sessionStorage.getItem(AUTH_SESSION_KEY)) {
    sessionStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
  }
}

export function clearAuthSessionStorage() {
  sessionStorage.removeItem(AUTH_SESSION_KEY);
  sessionStorage.removeItem(LAST_ACTIVITY_KEY);
  sessionStorage.removeItem(TOKEN_VERIFIED_KEY);
  sessionStorage.removeItem(AUTH_TOKEN_KEY);
  sessionStorage.removeItem(AUTH_SUPERUSER_KEY);
  sessionStorage.removeItem(AUTH_PERMISSIONS_KEY);
}

export function markAuthTokenVerified(token) {
  if (token) {
    sessionStorage.setItem(TOKEN_VERIFIED_KEY, String(token));
  }
}

export function isAuthTokenVerified(token) {
  return Boolean(token) && sessionStorage.getItem(TOKEN_VERIFIED_KEY) === String(token);
}

export function clearAuthCookiesFromDocument() {
  AUTH_COOKIE_KEYS.forEach((name) => {
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  });
}

export function removeAuthCookies(removeCookie) {
  AUTH_COOKIE_KEYS.forEach((key) => {
    removeCookie(key, { path: '/' });
  });
}

export function isLoginPath(pathname = window.location.pathname) {
  return pathname === '/' || pathname === '';
}

export function redirectUnauthenticatedDirectOpen(removeCookie) {
  if (typeof window === 'undefined') return false;
  if (isLoginPath()) return false;
  if (isAuthSessionValid()) return false;
  invalidateAuthSession(removeCookie);
  window.location.replace('/');
  return true;
}

export function invalidateAuthSession(removeCookie) {
  clearAuthSessionStorage();
  if (removeCookie) {
    removeAuthCookies(removeCookie);
  } else {
    clearAuthCookiesFromDocument();
  }
}

export function isAuthSessionValid(token) {
  const resolvedToken = token || sessionStorage.getItem(AUTH_TOKEN_KEY) || '';
  if (!resolvedToken) {
    return false;
  }
  if (!sessionStorage.getItem(AUTH_SESSION_KEY)) {
    return false;
  }
  const lastActivity = Number(sessionStorage.getItem(LAST_ACTIVITY_KEY) || 0);
  if (!lastActivity) {
    return false;
  }
  return Date.now() - lastActivity < AUTH_IDLE_TIMEOUT_MS;
}

export function getAuthIdleRemainingMs() {
  const lastActivity = Number(sessionStorage.getItem(LAST_ACTIVITY_KEY) || 0);
  if (!lastActivity) {
    return 0;
  }
  return Math.max(0, AUTH_IDLE_TIMEOUT_MS - (Date.now() - lastActivity));
}

export function getPermissionList(categoryCookies) {
  const fromCookie = normalizePermissionList(categoryCookies?.myCategory);
  if (fromCookie.length) {
    return fromCookie;
  }
  return readStoredPermissions();
}

export function hasPermission(categoryCookies, permissionKey) {
  return getPermissionList(categoryCookies).includes(permissionKey);
}

export function isSuperuser(superuserCookies) {
  if (readStoredSuperuser()) {
    return true;
  }
  return isTruthyFlag(superuserCookies?.mySuperuser);
}

export function canAccessModule(categoryCookies, superuserCookies, modulePermission) {
  return isSuperuser(superuserCookies) || hasPermission(categoryCookies, modulePermission);
}

export function hasResolvedAuthProfile(categoryCookies, superuserCookies) {
  return isSuperuser(superuserCookies) || getPermissionList(categoryCookies).length > 0;
}
